/**
 * Today's Outlook connection, as the approved prototype shows it (2026-09-30): a compact status control in the
 * masthead's scope row ("● Outlook connected ⌄") that opens an overlay popover (nothing below moves; a press outside
 * or Escape closes it, Escape returns focus to the control), and, while the caller's own Outlook needs connecting, a
 * full-width alert under the masthead with Connect Outlook.
 *
 * Connecting asks tracker-api for a one-time URL (an authenticated POST, so the bearer token never goes in a URL) and
 * navigates to it; Microsoft sends the browser back to Today with ?outlook=<outcome>. Connecting only lets the portal
 * create drafts in the caller's own mailbox: nothing is ever sent, and in build mode every draft goes to the build
 * tester. Check connection asks Graph and says the answer in the popover; Disconnect goes through a confirm dialog.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../api/client.ts";
import { describeError, useAction, usePortal, type Load } from "../api/hooks.ts";
import type { GraphStatus } from "../api/schemas.ts";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import { bookingsNeedReconnect, checkFailureText, outlookState, type OutlookState } from "../components/OutlookConnection.tsx";
import { useToast } from "../components/Toast.tsx";

type GraphLoad = Load<GraphStatus> & { reload: () => void };

const TITLE: Readonly<Record<OutlookState, string>> = {
  checking: "Checking Outlook…", connected: "Outlook connected", reconnect: "Outlook needs attention", off: "Outlook needs attention",
  unavailable: "Outlook not set up", unmapped: "Outlook not connected", preview: "Outlook not in preview", error: "Outlook status unavailable",
};
const TONE: Readonly<Record<OutlookState, "good" | "warn" | "off">> = {
  checking: "off", connected: "good", reconnect: "warn", off: "warn", unavailable: "off", unmapped: "off", preview: "off", error: "warn",
};

/** The one-time connect URL, then the browser goes to Microsoft. A failure says so in a toast. */
export function useOutlookConnect(go: (url: string) => void = (url) => window.location.assign(url)) {
  const act = useAction();
  const toast = useToast();
  const connect = async () => {
    if (act.busy) return;
    const url = await act.run((api) => api.graphConnect());
    if (url) go(url);
    else toast.show({ tone: "err", title: "Outlook couldn't be connected", desc: "The connect link didn't come back. Nothing changed; try again.", action: { label: "Try again", onAction: () => void connect() } });
  };
  return { connect, busy: act.busy };
}

/** Open the scope row's Outlook popover and put focus on its control (the draft-failure toast's "Go to Outlook"). */
export function openTodayOutlook() {
  const d = document.getElementById("today-outlook") as HTMLDetailsElement | null;
  if (!d) return;
  d.open = true;
  d.querySelector("summary")?.focus({ preventScroll: true });
}

export function TodayOutlookControl({ load, go }: { load: GraphLoad; go?: (url: string) => void }) {
  const { api, expire, buildMode = true } = usePortal();
  const state = outlookState(load);
  const ref = useRef<HTMLDetailsElement>(null);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState("");
  const [checking, setChecking] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const act = useAction();
  const toast = useToast();
  const { connect, busy } = useOutlookConnect(go);

  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node) && !(e.target as Element | null)?.closest?.("dialog")) ref.current.open = false; };
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !ref.current?.open || (e.target as Element | null)?.closest?.("dialog")) return;
      e.preventDefault();
      ref.current.open = false;
      ref.current.querySelector("summary")?.focus({ preventScroll: true });
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", key, true);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", key, true); };
  }, [open]);

  const check = async () => {
    if (checking) return;
    setChecking(true);
    setResult("Checking the connection…");
    try {
      const c = await api.graphCheck();
      setResult(c.ok ? `The connection is working for ${c.upn}.` : checkFailureText(c));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) expire();
      setResult(`${describeError(e instanceof Error ? e : new Error(String(e)))} Drafts wait; nothing is lost.`);
    } finally { setChecking(false); }
  };
  const disconnect = async () => {
    setConfirm(false);
    const done = await act.run(async (a) => { await a.graphDisconnect(); return true; });
    if (done) {
      toast.show({ tone: "ok", title: "Outlook disconnected", desc: "No new drafts are created until you connect again. Drafts already in Outlook stay where they are." });
      load.reload();
    } else {
      toast.show({ tone: "err", title: "Outlook wasn't disconnected", desc: "Nothing changed. Try again in a minute.", action: { label: "Try again", onAction: () => setConfirm(true) } });
    }
  };

  const s = load.state === "ready" ? load.data : null;
  // AIC-521: connected, but the old connection can't take vendor bookings: the summary warns instead of showing green.
  const bookingsWarn = state === "connected" && bookingsNeedReconnect(s);
  let copy: ReactNode;
  let actions: ReactNode = null;
  if (state === "connected") {
    copy = buildMode ? <>Build drafts go to the tester’s Outlook.<br />No email is sent automatically.</> : <>Drafts go to your Outlook Drafts{s?.upn ? ` (${s.upn})` : ""}.<br />No email is sent for you: you send every draft.</>;
    // AIC-521: the old connection lacks the calendar permission, so vendors can't book a call. Settings (admin only)
    // says so too; an analyst without Settings only learns it here.
    if (bookingsWarn) {
      copy = <>{copy}<br /><b>Reconnect Outlook to enable bookings.</b> Until then vendors can only send you a message, not book a call.</>;
    }
    actions = <>{bookingsWarn ? <button type="button" className="ipd-btn ipd-btn--go" aria-busy={busy || undefined} aria-disabled={busy || undefined} onClick={() => void connect()}>Reconnect Outlook</button> : null}<button type="button" className="ipd-btn" aria-busy={checking || undefined} onClick={() => void check()}>Check connection</button><button type="button" className="ipd-btn ipd-btn--text" onClick={() => setConfirm(true)}>Disconnect</button></>;
  } else if (state === "off" || state === "reconnect") {
    copy = state === "off" ? <>Connect Outlook to create drafts.<br />You can keep reviewing your work.</> : <>Microsoft no longer accepts your saved sign-in{s?.upn ? ` for ${s.upn}` : ""}.<br />Reconnect to create drafts. You can keep reviewing your work.</>;
    actions = <button type="button" className="ipd-btn" aria-busy={busy || undefined} aria-disabled={busy || undefined} onClick={() => void connect()}>{state === "off" ? "Connect Outlook" : "Reconnect Outlook"}</button>;
  } else if (state === "checking") copy = "Checking your Outlook connection…";
  else if (state === "error") {
    copy = <>{load.state === "error" ? describeError(load.error) : ""} Drafts wait until the check works.</>;
    actions = <button type="button" className="ipd-btn" onClick={load.reload}>Try again</button>;
  } else if (state === "unavailable") copy = "Connecting Outlook isn't set up on this server yet.";
  else if (state === "preview") copy = "Not available in preview. The analyst preview never opens the analyst’s Outlook connection or mailbox.";
  else copy = "You can connect once an admin maps your account to a Software Tracker analyst.";

  const title = bookingsWarn ? "Outlook: reconnect for bookings" : TITLE[state];
  const tone = bookingsWarn ? "warn" : TONE[state];
  return (
    <details className="today-outlook" id="today-outlook" ref={ref} data-ol={state} onToggle={(e) => { setOpen(e.currentTarget.open); if (!e.currentTarget.open) setResult(""); }}>
      <summary aria-label={`${title}. Outlook connection details`}><span className={`today-dot today-dot--${tone}`} aria-hidden="true" /><span>{title}</span></summary>
      <div className="today-popover" role="group" aria-label="Outlook connection">
        <strong>Outlook connection</strong>
        <p>{copy}</p>
        {actions ? <div className="today-popover-actions">{actions}</div> : null}
        <p className="today-popover-result" role="status">{result}</p>
      </div>
      <ConfirmDialog open={confirm} title="Disconnect Outlook?" cancelLabel="Keep connected" confirmLabel="Disconnect" onCancel={() => setConfirm(false)} onConfirm={() => void disconnect()}
        body="Software Tracker will stop creating invite and reminder drafts in your Drafts folder until you reconnect. Drafts already in Outlook stay where they are." />
    </details>
  );
}

/** The full-width notice under the masthead while the caller's own Outlook needs connecting (prototype #outlookAlert). */
export function TodayOutlookAlert({ state, go }: { state: OutlookState; go?: (url: string) => void }) {
  const { connect, busy } = useOutlookConnect(go);
  if (state !== "off" && state !== "reconnect") return null;
  return (
    <section className="today-alert" aria-labelledby="today-alert-h">
      <div><strong id="today-alert-h">{state === "off" ? "Connect Outlook to create invite drafts." : "Reconnect Outlook to create invite drafts."}</strong><p>Your reviews are ready to work on while you reconnect.</p></div>
      <button type="button" className="ipd-btn" aria-busy={busy || undefined} aria-disabled={busy || undefined} onClick={() => void connect()}>{state === "off" ? "Connect Outlook" : "Reconnect Outlook"}</button>
    </section>
  );
}
